import React, { useMemo } from "react";
import { TimelineStep } from "../../types/timeline";
import { ViewerSettings } from "../../types/settings";

interface StepVisibilityPanelProps {
  isOpen?: boolean;
  steps?: TimelineStep[];
  selectedStepId?: string | null;
  onSelectStep?: (stepId: string) => void;
  onViewStepData?: (stepId: string) => void;
  onClearAll?: () => void;
  panelTitle?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  titleColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  iconColor?: string;
  activeColor?: string;
  fontFamily?: string;
  language?: ViewerSettings["language"];
}

function getCopy(language: ViewerSettings["language"]) {
  switch (language) {
    case "es":
      return {
        title: "Visibilidad",
        items: "pasos",
        viewDataFor: "Ver datos de",
        start: "Inicio",
        end: "Fin",
      };
    case "fr":
      return {
        title: "Visibilit\u00e9",
        items: "\u00e9tapes",
        viewDataFor: "Voir les donn\u00e9es de",
        start: "D\u00e9but",
        end: "Fin",
      };
    case "de":
      return {
        title: "Sichtbarkeit",
        items: "Schritte",
        viewDataFor: "Daten anzeigen f\u00fcr",
        start: "Start",
        end: "Ende",
      };
    case "zh":
      return {
        title: "\u53ef\u89c1\u6027",
        items: "\u6b65\u9aa4",
        viewDataFor: "\u67e5\u770b\u6570\u636e",
        start: "\u5f00\u59cb",
        end: "\u7ed3\u675f",
      };
    case "en":
    default:
      return {
        title: "Step Visibility",
        items: "steps",
        viewDataFor: "View data for",
        start: "Start",
        end: "End",
      };
  }
}

export function StepVisibilityPanel({
  isOpen = false,
  steps = [],
  selectedStepId = null,
  onSelectStep,
  onViewStepData,
  onClearAll,
  panelTitle,
  backgroundColor = "rgba(55, 55, 55, 0.85)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
  titleColor = "rgb(56, 189, 248)",
  textColor = "rgba(255, 255, 255, 0.9)",
  mutedTextColor = "rgba(255, 255, 255, 0.45)",
  iconColor = "rgba(255, 255, 255, 0.75)",
  activeColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
  language = "en",
}: StepVisibilityPanelProps) {
  const copy = getCopy(language);
  const orderedSteps = useMemo(
    () => [...steps].sort((a, b) => a.order - b.order),
    [steps],
  );

  if (!isOpen) return null;

  return (
    <aside
      style={{
        position: "absolute",
        left: "1.5rem",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 100,
        width: "18rem",
        height: "calc(100% - 12rem)",
        maxHeight: "700px",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          borderRadius: "1.5rem",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          fontFamily,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: `${titleColor}CC`,
            }}
          >
            {panelTitle || copy.title}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {onClearAll && (
              <button
                type="button"
                onClick={onClearAll}
                title="Clear all visibility assignments"
                aria-label="Clear all visibility assignments"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "1.75rem",
                  height: "1.75rem",
                  borderRadius: "0.375rem",
                  border: `1px solid ${borderColor}`,
                  background: "transparent",
                  color: mutedTextColor,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "1rem", fontWeight: 300 }}
                >
                  delete_sweep
                </span>
              </button>
            )}
            <span
              style={{
                fontSize: "0.625rem",
                padding: "0.25rem 0.5rem",
                borderRadius: "0.25rem",
                color: mutedTextColor,
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              }}
            >
              {orderedSteps.length} {copy.items}
            </span>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
          }}
        >
          {orderedSteps.map((step) => {
            const isActive = step.id === selectedStepId;
            return (
              <div
                key={step.id}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  padding: "0.75rem",
                  borderRadius: "0.75rem",
                  cursor: "pointer",
                  border: isActive
                    ? `1px solid ${activeColor}4D`
                    : "1px solid transparent",
                  background: isActive ? `${activeColor}1A` : "transparent",
                  color: isActive ? activeColor : textColor,
                  transition: "all 0.2s",
                  textAlign: "left",
                }}
              >
                <button
                  type="button"
                  onClick={() => onSelectStep?.(step.id)}
                  aria-label={step.name}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                    textAlign: "left",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 300,
                      color: isActive ? activeColor : iconColor,
                      flexShrink: 0,
                    }}
                  >
                    visibility
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        color: isActive ? activeColor : textColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {step.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.625rem",
                        color: isActive ? `${activeColor}99` : mutedTextColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily:
                          "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace",
                      }}
                    >
                      {step.startFrame}-{step.endFrame}
                    </span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onViewStepData?.(step.id);
                  }}
                  style={{
                    width: "32px",
                    height: "32px",
                    background: "transparent",
                    color: mutedTextColor,
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  aria-label={`${copy.viewDataFor} ${step.name}`}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1.25rem", fontWeight: 300 }}
                  >
                    visibility_lock
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
